import { useEffect, useRef, useCallback } from 'react';
import { Vector as VectorLayer } from 'ol/layer';
import { Vector as VectorSource } from 'ol/source';
import Feature from 'ol/Feature'; // Correct default import for Feature
import Point from 'ol/geom/Point'; // Correct default import for Point
import { Style, Icon } from 'ol/style';
import { Map as OLMap } from 'ol';

export const useSearchMarkerLayer = (map: OLMap | null) => {
  const markerSourceRef = useRef<VectorSource | null>(null);
  const markerLayerRef = useRef<VectorLayer<VectorSource> | null>(null);

  // Lazy init the source to avoid creating it on every render
  if (!markerSourceRef.current) {
    markerSourceRef.current = new VectorSource();
  }

  // Initialize Layer
  useEffect(() => {
    if (!map) return; // Wait for map to be available

    const layer = new VectorLayer({
      source: markerSourceRef.current!,
      zIndex: 10000, // Topmost
      style: new Style({
        image: new Icon({
          anchor: [0.5, 1],
          anchorXUnits: 'fraction',
          anchorYUnits: 'fraction',
          src: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTEyIDJDOC4xMyAyIDUgNS4xMyA1IDlDNSAxNC4yNSAxMiAyMiAxMiAyMkMxMiAyMiAxOSAxNC4yNSAxOSA5QzE5IDUuMTMgMTUuODcgMiAxMiAyWk0xMiAxMS41QzEwLjYyIDExLjUgOS41IDEwLjM4IDkuNSA5QzkuNSA3LjYyIDEwLjYyIDYuNSAxMiA2LjVDMTMuMzggNi41IDE0LjUgNy42MiAxNC41IDlDMTQuNSAxMC4zOCAxMy4zOCAxMS41IDEyIDExLjVaIiBmaWxsPSIjZmYwMDAwIi8+Cjwvc3ZnPgo=',
          scale: 1,
        }),
      }),
      properties: { id: 'marker' },
    });

    map.addLayer(layer);
    markerLayerRef.current = layer;

    return () => {
      if (map && markerLayerRef.current) {
        map.removeLayer(markerLayerRef.current);
        markerLayerRef.current = null;
      }
    };
  }, [map]);

  const addMarker = useCallback((coordinates: number[]) => {
    const source = markerSourceRef.current;
    if (!source) return;
    source.clear();
    const feature = new Feature({
      geometry: new Point(coordinates),
    });
    source.addFeature(feature);
  }, []);

  const clearMarker = useCallback(() => {
    markerSourceRef.current?.clear();
  }, []);

  return { addMarker, clearMarker };
};
